import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { MsalProvider } from "@azure/msal-react";
import { msalInstance } from "./auth/auth-config";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "./index.scss";
import App from "./App.tsx";
import { startSentry } from "./utilities/sentry.ts";

startSentry();

/**
 * Defaults, rather than react-query's.
 *
 * `new QueryClient()` leaves refetchOnWindowFocus ON. Returning to the tab then
 * refires every mounted query at once, and on a screen that pops a modal when a
 * query rejects that is a "Something Went Wrong" for coming back from lunch.
 * It is also how a grid full of unsaved edits can be refetched out from under
 * the person who made them.
 *
 * A pricing grid is not a live feed: the user searched for it, and it should
 * change when they ask it to, not when the window regains focus.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      // A transport blip should not surface as an error on the first try.
      retry: 1,
      staleTime: 30_000,
    },
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <MsalProvider instance={msalInstance}>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </MsalProvider>
  </StrictMode>
);
